import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { ApiError } from '@/api/http/types'
import { formatLockoutCountdown, lockoutFromApiError } from '../lib/lockout'
import { useLockoutRemaining } from './useLockoutRemaining'
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
  FORGOT_PW_INVALID_EMAIL_MSG,
  FORGOT_PW_RATE_LIMIT_MSG,
} from '../lib'
import { FORGOT_PW_EMAIL_NOT_FOUND_MSG } from '../styles/forgotPasswordStyles'
import { runDetached } from '@/shared/lib/runDetachedPromise'

export type ForgotPasswordEmailError =
  | { kind: 'format'; message: string }
  | { kind: 'not-found'; message: string }
  | { kind: 'server'; message: string }

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

export function getForgotPasswordServerEmailError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'An unexpected error occurred. Please try again.'
  }
  if (error.isRateLimited) {
    return FORGOT_PW_RATE_LIMIT_MSG
  }
  if (error.isNotFound) {
    return FORGOT_PW_EMAIL_NOT_FOUND_MSG
  }
  const message = error.message.toLowerCase()
  if (message.includes('valid email') || message.includes('enter a valid')) {
    return FORGOT_PW_INVALID_EMAIL_MSG
  }
  if (message.includes('not found') || message.includes('email not found')) {
    return FORGOT_PW_EMAIL_NOT_FOUND_MSG
  }
  return error.message
}

function formatErrorForValue(value: string): ForgotPasswordEmailError | null {
  if (value.length === 0) {
    return null
  }
  const parsed = forgotPasswordSchema.safeParse({ email: value })
  if (parsed.success) {
    return null
  }
  const issue = parsed.error.flatten().fieldErrors.email?.[0]
  return {
    kind: 'format',
    message: issue ?? FORGOT_PW_INVALID_EMAIL_MSG,
  }
}

/** Client-side email format/required only while typing; password-reset API runs on Send Email only. */
export function useForgotPasswordEmailField(
  form: Pick<
    UseFormReturn<ForgotPasswordFormData>,
    'register' | 'setError' | 'clearErrors' | 'trigger'
  >
) {
  const { register, setError, clearErrors, trigger } = form
  const [fieldError, setFieldError] = useState<ForgotPasswordEmailError | null>(null)
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null)
  const rateLimitMessageRef = useRef<string | null>(null)
  const hadActiveLockoutRef = useRef(false)
  const lockoutRemaining = useLockoutRemaining(lockoutUntilMs)
  const notFoundForEmailRef = useRef<string | null>(null)

  const applyFieldError = useCallback(
    (error: ForgotPasswordEmailError | null) => {
      setFieldError(error)
      if (error?.kind !== 'server') {
        setLockoutUntilMs(null)
        rateLimitMessageRef.current = null
      }
      if (error) {
        setError('email', { type: 'manual', message: error.message })
      } else {
        clearErrors('email')
      }
    },
    [clearErrors, setError]
  )

  const emailRegistration = register('email', {
    onChange: (event) => {
      const value = event.target.value
      const normalized = normalizeEmail(value)
      if (notFoundForEmailRef.current && notFoundForEmailRef.current !== normalized) {
        notFoundForEmailRef.current = null
      }
      const formatError = formatErrorForValue(value)
      if (formatError) {
        applyFieldError(formatError)
      } else {
        setFieldError((current) => {
          if (current?.kind === 'not-found' || current?.kind === 'server') {
            clearErrors('email')
            setLockoutUntilMs(null)
            rateLimitMessageRef.current = null
            return null
          }
          if (current?.kind === 'format') {
            clearErrors('email')
            return null
          }
          return current
        })
      }
      runDetached(trigger('email'))
    },
  })

  const setServerError = useCallback(
    (message: string, kind: 'not-found' | 'server' = 'server', email?: string) => {
      if (kind === 'not-found' && email) {
        notFoundForEmailRef.current = normalizeEmail(email)
      }
      applyFieldError({ kind, message })
    },
    [applyFieldError]
  )

  const clearEmailErrors = useCallback(() => {
    notFoundForEmailRef.current = null
    applyFieldError(null)
  }, [applyFieldError])

  const applySubmitValidationErrors = useCallback(
    (message: string | undefined) => {
      if (message) {
        applyFieldError({ kind: 'format', message })
      }
    },
    [applyFieldError]
  )

  const handleMutationError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.isRateLimited) {
        const lockout = lockoutFromApiError(error)
        if (lockout) {
          rateLimitMessageRef.current = FORGOT_PW_RATE_LIMIT_MSG
          setLockoutUntilMs(lockout.lockedUntilMs)
        }
      }
      const message = getForgotPasswordServerEmailError(error)
      const kind =
        message === FORGOT_PW_EMAIL_NOT_FOUND_MSG ? 'not-found' : 'server'
      applyFieldError({ kind, message })
    },
    [applyFieldError]
  )

  useEffect(() => {
    if (lockoutRemaining > 0) {
      hadActiveLockoutRef.current = true
      return
    }
    if (!hadActiveLockoutRef.current || lockoutUntilMs === null) {
      return
    }
    hadActiveLockoutRef.current = false
    setLockoutUntilMs(null)
    rateLimitMessageRef.current = null
    setFieldError((current) => (current?.kind === 'server' ? null : current))
    clearErrors('email')
  }, [clearErrors, lockoutRemaining, lockoutUntilMs])

  const emailErrorMessage = useMemo(() => {
    if (lockoutRemaining > 0 && rateLimitMessageRef.current) {
      return `${rateLimitMessageRef.current} (${formatLockoutCountdown(lockoutRemaining)})`
    }
    return fieldError?.message ?? null
  }, [fieldError?.message, lockoutRemaining])

  return {
    emailRegistration,
    fieldError,
    hasEmailError: Boolean(fieldError) || lockoutRemaining > 0,
    emailErrorMessage,
    setServerError,
    clearEmailErrors,
    handleMutationError,
    applySubmitValidationErrors,
  }
}
